'use client';

import {
  forwardRef,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import { startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Tooltip } from '@mokoboko/components';
import { mergeClassNames } from '@/lib/utils';
import { DayButton } from './day-button';
import {
  toDateKey,
  getFirstAvailableDate,
  formatMonthYear,
} from './calendar-utils';
import { CalendarContext, BookingContext, useCalendarContext, useBookingContext } from './calendar-context';
import type {
  CalendarRootProps,
  CalendarGridProps,
  CalendarContextValue,
  BookingContextValue,
  CalendarSlot,
} from './calendar.types';

/** Type-safe select dispatcher — narrows mode → onSelect without `as` assertions. */
function dispatchSelect(
  props: CalendarRootProps,
  value: Date | DateRange | Date[]
): void {
  if (!props.onSelect) return;
  if (props.mode === 'range') {
    if (!(value instanceof Date) && !Array.isArray(value)) props.onSelect(value);
  } else if (props.mode === 'multi') {
    if (Array.isArray(value)) props.onSelect(value);
  } else {
    if (value instanceof Date) props.onSelect(value);
  }
}

const Root = forwardRef<HTMLDivElement, CalendarRootProps>(
  (allProps, ref) => {
    const {
      mode = 'single',
      selected: controlledSelected,
      onSelect: _onSelect,
      locale = 'en-US',
      month: monthProp,
      onMonthChange: onMonthChangeProp,
      minDate: minDateProp,
      maxDate: maxDateProp,
      disabled: disabledProp,
      availability,
      selectedSlot: controlledSlot,
      onSlotSelect,
      onBook,
      bookLabel = 'Continue',
      showSpots = false,
      spotsUrgencyOnly = true,
      showFullyBooked = true,
      formatSlotTime,
      isProcessing = false,
      isSlotWaitlistJoinable = () => false,
      isSlotBooked = () => false,
      showInstructor = true,
      soleInstructor = false,
      className,
      children,
      ...props
    } = allProps;

    const isBooking = !!availability;

    const propsRef = useRef(allProps);
    propsRef.current = allProps;

    const minDate = minDateProp ?? (isBooking ? startOfDay(new Date()) : null);
    const maxDate = maxDateProp ?? null;

    const [internalSelected, setInternalSelected] = useState<
      Date | DateRange | Date[] | null
    >(() => {
      if (controlledSelected !== undefined) return null;
      if (isBooking && mode === 'single') return getFirstAvailableDate(availability);
      return null;
    });

    const [internalSlot, setInternalSlot] = useState<CalendarSlot | null>(null);

    const [internalMonth, setInternalMonth] = useState(() => {
      if (monthProp) return monthProp;
      if (controlledSelected && mode === 'single' && controlledSelected instanceof Date) {
        return controlledSelected;
      }
      if (isBooking) {
        const first = getFirstAvailableDate(availability);
        return first ?? new Date();
      }
      return new Date();
    });

    const currentMonth = monthProp ?? internalMonth;

    const setCurrentMonth = useCallback((d: Date) => {
      onMonthChangeProp?.(d);
      if (monthProp === undefined) setInternalMonth(d);
    }, [onMonthChangeProp, monthProp]);

    const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;

    const selectDate = useCallback(
      (value: Date | DateRange | Date[]) => {
        dispatchSelect(propsRef.current, value);
        if (controlledSelected === undefined) setInternalSelected(value);
        if (controlledSlot === undefined) setInternalSlot(null);
      },
      [controlledSelected, controlledSlot]
    );

    const sharedCtx = useMemo(
      () => ({
        currentMonth,
        setCurrentMonth,
        selectDate,
        locale,
        minDate,
        maxDate,
        disabled: disabledProp ?? null,
      }),
      [currentMonth, setCurrentMonth, selectDate, locale, minDate, maxDate, disabledProp]
    );

    const calendarCtx: CalendarContextValue = useMemo(() => {
      if (mode === 'range') {
        return {
          ...sharedCtx,
          mode,
          selected: (selected !== null && !(selected instanceof Date) && !Array.isArray(selected) ? selected : null),
        };
      }
      if (mode === 'multi') {
        return {
          ...sharedCtx,
          mode,
          selected: Array.isArray(selected) ? selected : [],
        };
      }
      return {
        ...sharedCtx,
        mode: 'single' as const,
        selected: selected instanceof Date ? selected : null,
      };
    }, [mode, sharedCtx, selected]);

    const selectedSlot = controlledSlot !== undefined ? controlledSlot : internalSlot;

    const selectSlot = useCallback(
      (s: CalendarSlot) => {
        const next = selectedSlot?.id === s.id ? null : s;
        onSlotSelect?.(next);
        if (controlledSlot === undefined) setInternalSlot(next);
      },
      [onSlotSelect, controlledSlot, selectedSlot]
    );

    const handleBook = useCallback(() => {
      if (selected && selected instanceof Date && selectedSlot) {
        onBook?.(selected, selectedSlot);
      }
    }, [selected, selectedSlot, onBook]);

    const bookingCtx: BookingContextValue | null = isBooking
      ? { availability, selectedSlot, selectSlot, handleBook, bookLabel, showSpots, spotsUrgencyOnly, showFullyBooked, formatSlotTime, isProcessing, isSlotWaitlistJoinable, isSlotBooked, showInstructor, soleInstructor }
      : null;

    const content = (
      <div
        ref={ref}
        className={mergeClassNames('flex flex-wrap gap-4 md:gap-8', className)}
        {...props}
      >
        {children}
      </div>
    );

    return (
      <CalendarContext.Provider value={calendarCtx}>
        {bookingCtx ? (
          <BookingContext.Provider value={bookingCtx}>
            {content}
          </BookingContext.Provider>
        ) : (
          content
        )}
      </CalendarContext.Provider>
    );
  }
);
Root.displayName = 'Calendar.Root';

export { dispatchSelect };

function useNavDisabled(ariaDisabled: React.AriaAttributes['aria-disabled']) {
  return ariaDisabled === true || ariaDisabled === 'true';
}

function CustomPrevButton({ 'aria-disabled': ariaDisabled, className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const isDisabled = useNavDisabled(ariaDisabled);
  const booking = useBookingContext();

  const button = (
    <Button
      variant="secondary-gray"
      size="md"
      iconOnly
      aria-label="Previous month"
      disabled={isDisabled}
      className={mergeClassNames(
        isDisabled && '[&_svg]:!text-gray-600 !pointer-events-auto !cursor-not-allowed',
      )}
      {...props}
    >
      <ChevronLeft />
    </Button>
  );

  if (!isDisabled || !booking) return <div className={className}>{button}</div>;

  return (
    <div className={className}>
      <Tooltip
        content="Can't book classes in the past :)"
        side="bottom"
      >
        {button}
      </Tooltip>
    </div>
  );
}

function CustomNextButton({ 'aria-disabled': ariaDisabled, className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const isDisabled = useNavDisabled(ariaDisabled);
  return (
    <div className={className}>
      <Button
        variant="secondary-gray"
        size="md"
        iconOnly
        aria-label="Next month"
        disabled={isDisabled}
        className={mergeClassNames(isDisabled && '[&_svg]:!text-gray-600')}
        {...props}
      >
        <ChevronRight />
      </Button>
    </div>
  );
}

const DAY_PICKER_COMPONENTS = {
  DayButton,
  PreviousMonthButton: CustomPrevButton,
  NextMonthButton: CustomNextButton,
} as const;

const DAY_PICKER_CLASS_NAMES = {
  root: 'w-full',
  months: 'w-full',
  month: 'w-full grid grid-cols-[1fr_auto_1fr] items-center gap-x-1 pt-3',
  month_grid: 'block w-full col-span-3 pt-4 [&_thead]:block [&_thead]:w-full [&_tbody]:block [&_tbody]:w-full [&_tr]:grid [&_tr]:grid-cols-7 [&_tr]:gap-x-1.5 sm:[&_tr]:gap-x-0 md:[&_tr]:gap-x-1',
  month_caption:
    'flex items-center justify-center col-start-2 row-start-1 min-w-0 min-[301px]:min-w-[170px]',
  caption_label:
    'heading-sub select-none whitespace-nowrap',
  button_previous:
    'col-start-1 row-start-1 justify-self-end shrink-0',
  button_next:
    'col-start-3 row-start-1 justify-self-start shrink-0',
  weekday:
    'flex items-center justify-center h-10 text-sm leading-5 font-semibold text-gray-900',
  day: 'flex items-center justify-center py-1 sm:py-1.5 sm:px-1',
  day_button: '',
  outside: '',
  disabled: '',
  today: '',
  selected: '',
  range_start: '',
  range_end: '',
  range_middle: '',
} as const;

const Grid = forwardRef<HTMLDivElement, CalendarGridProps>(
  ({ className, ...props }, ref) => {
    const ctx = useCalendarContext();
    const booking = useBookingContext();

    const availableDates = useMemo(() => {
      if (!booking) return [];
      return Object.keys(booking.availability)
        .filter((key) => booking.availability[key].length > 0)
        .map((key) => {
          const [y, m, d] = key.split('-').map(Number);
          return new Date(y, m - 1, d);
        });
    }, [booking]);

    const disabledMatcher = useCallback(
      (date: Date) => {
        if (ctx.minDate && date < ctx.minDate) return true;
        if (ctx.maxDate && date > ctx.maxDate) return true;
        if (ctx.disabled?.(date)) return true;
        if (booking) {
          const key = toDateKey(date);
          return !booking.availability[key]?.length;
        }
        return false;
      },
      [ctx.minDate, ctx.maxDate, ctx.disabled, booking]
    );

    const handleSelectSingle = useCallback(
      (date: Date | undefined) => {
        if (date) ctx.selectDate(date);
      },
      [ctx.selectDate]
    );

    const handleSelectRange = useCallback(
      (range: DateRange | undefined) => {
        if (range) ctx.selectDate(range);
      },
      [ctx.selectDate]
    );

    const handleSelectMulti = useCallback(
      (dates: Date[] | undefined) => {
        if (dates) ctx.selectDate(dates);
      },
      [ctx.selectDate]
    );

    const formatters = useMemo(
      () => ({
        formatCaption: (month: Date) => formatMonthYear(month, ctx.locale),
        formatWeekdayName: (weekday: Date) =>
          new Intl.DateTimeFormat(ctx.locale, { weekday: 'short' })
            .format(weekday)
            .toUpperCase()
            .replace('.', ''),
      }),
      [ctx.locale]
    );

    const modifiers = useMemo(
      () => (booking ? { available: availableDates } : {}),
      [booking, availableDates]
    );

    const sharedProps = {
      navLayout: 'around' as const,
      month: ctx.currentMonth,
      onMonthChange: ctx.setCurrentMonth,
      startMonth: ctx.minDate ?? undefined,
      endMonth: ctx.maxDate ?? undefined,
      weekStartsOn: 1 as const,
      fixedWeeks: false,
      showOutsideDays: true,
      disabled: disabledMatcher,
      modifiers,
      formatters,
      components: DAY_PICKER_COMPONENTS,
      classNames: DAY_PICKER_CLASS_NAMES,
    };

    let picker: React.ReactNode;

    if (ctx.mode === 'range') {
      picker = (
        <DayPicker
          mode="range"
          selected={ctx.selected ?? undefined}
          onSelect={handleSelectRange}
          {...sharedProps}
        />
      );
    } else if (ctx.mode === 'multi') {
      picker = (
        <DayPicker
          mode="multiple"
          selected={ctx.selected ?? undefined}
          onSelect={handleSelectMulti}
          {...sharedProps}
        />
      );
    } else {
      picker = (
        <DayPicker
          mode="single"
          selected={ctx.selected ?? undefined}
          onSelect={handleSelectSingle}
          {...sharedProps}
        />
      );
    }

    return (
      <div ref={ref} className={mergeClassNames('relative flex-1 basis-full md:basis-[290px] min-w-0', className)} {...props}>
        {picker}
      </div>
    );
  }
);
Grid.displayName = 'Calendar.Grid';

export { Root, Grid };
